import React, { useEffect, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Platform, Linking, Pressable, Text } from 'react-native';
import I18n from '../../../components/shared/i18n/I18n.component';
import colors from '../../../config/colors';
import ImageIcon from '../../../components/shared/Icon/ImageIcon.component';
import useDevicePlatform, {
	DeviceType,
} from '../../../hooks/useDevicePlatform.hook';
import { WebView } from 'react-native-web-webview';
import { WebView as NativeWebView } from 'react-native-webview';
import CheckBox from '@react-native-community/checkbox';
import { Checkbox as MuiCheckbox, makeStyles } from '@material-ui/core';
import Button from '../../../components/shared/Buttons/Button.component';

const useStyles = makeStyles(() => ({
	checkbox: {
		marginRight: -10,

		color: '#6d7278',
		'&$checked': {
			color: colors.lightPrimary,
			backgroundColor: colors.white,
		},
	},
	checked: {},
}));

interface Props {
	campaignPages: any;
	onResponse?(ans: boolean): void;
	onNext(val: any): void;
	openWithButton?: boolean;
	enableUserDismissal: boolean;
}

export const UserCampaignModal = ({
	campaignPages,
	onResponse,
	onNext,
	openWithButton,
	enableUserDismissal,
}: Props) => {
	const [currentPageIndex, setCurrentPageIndex] = useState(0);
	const [isChecked, setIsChecked] = useState(false);
	const [isLastPage, setIsLastPage] = useState(false);
	const [isFirstPage, setIsFirstPage] = useState(false);
	const [isPrevButtonHovered, setIsPrevButtonHovered] = useState(false);
	const [isNextButtonHovered, setIsNextButtonHovered] = useState(false);

	const platform = useDevicePlatform();
	const isWeb = platform === DeviceType.WEB;
	const isIos = Platform.OS === 'ios';

	const classes = useStyles();

	const totalPages = campaignPages.length;

	useEffect(() => {
		setIsLastPage(currentPageIndex === totalPages - 1);
		setIsFirstPage(currentPageIndex === 0);
	}, [currentPageIndex, totalPages]);

	const nextPage = () => {
		if (currentPageIndex < totalPages - 1) {
			setCurrentPageIndex(currentPageIndex + 1);
		}
	};

	const prevPage = () => {
		if (currentPageIndex > 0) {
			setCurrentPageIndex(currentPageIndex - 1);
		}
	};

	const handleUserDismissed = () => {
		setIsChecked(!isChecked);
		if (onResponse) {
			onResponse(!isChecked);
		}
	};

	let html = campaignPages[currentPageIndex].data().content;
    const webHtml = `
        <!doctype html>
        <html>
          <head>
            <meta name="viewport" content="width=device-width, initial-scale=1" />
			<link href="https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;700&display=swap" rel="stylesheet">
            <style>
              html, body, div, p, span, li, h1, h2, h3, h4, h5, h6 {
                color: #525962 ;
                text-align: right ;
                font-family: Rubik, sans-serif ;
                font-size: 16px ;
                font-weight: normal ;
				line-height: 22px;
                margin: 0 ;
                padding: 0 ;
              }
              img, video, iframe { max-width: 100%; }
            </style>
          </head>
          <body>${html}</body>
        </html>
    `;

	return (
		<View style={[styles.modalContainer, { width: isWeb ? 600 : 350 }]}>
			<View style={{ gap: 8 }}>
				<View style={{ flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'center' }}>
					<I18n size={24} weight='normal' color='primary'>
						{campaignPages[currentPageIndex].data().title}
					</I18n>
					<Pressable onPress={() => onNext({type:"close"})} style={styles.closeButton}>
						<Text style={styles.closeButtonText}>×</Text>
					</Pressable>
					
				</View>
				<I18n color='text2Grey'>{campaignPages[currentPageIndex].data().subTitle}</I18n>
			</View>
			<View style={styles.divider} />

			{Platform.OS === 'web' ? (
				<WebView
					originWhitelist={['*']}
					source={{
						html: webHtml
					}}
					style={{ fontWeight: 'normal' }}
				/>
			) : (
				<NativeWebView
					scalesPageToFit={true}
					automaticallyAdjustContentInsets={true}
					originWhitelist={['*']}
					source={{
						html: webHtml
					}}
					// onShouldStartLoadWithRequest for ios links will open in defalut browser instead of webview component
					onShouldStartLoadWithRequest={event => {
						if (event.navigationType === 'click' && event.url.startsWith('http')) {
							Linking.openURL(event.url).catch(err => console.error('Failed to open URL:', err));
							return false
						}
						return true
					}}				
				/>
			)}
			<View
				style={[
					styles.navigationContainer,
					{
						flexDirection: enableUserDismissal
							? 'row-reverse'
							: 'row',
					},
				]}
			>
				{enableUserDismissal ? (
					<View style={styles.checkboxContainer}>
							<View style={styles.checkboxButton}>
								{Platform.OS === 'ios' ||
								Platform.OS === 'android' ? (
									<CheckBox
										value={isChecked}
										onValueChange={handleUserDismissed}
										boxType='square'
										onCheckColor={colors.white}
										onFillColor={colors.lightPrimary}
										onTintColor={colors.white}
										tintColors={{
											true: colors.lightPrimary,
										}}
										style={{
											width: 25,
											height: 22,
											borderRadius: 2,
										}}
									/>
								) : (
									<MuiCheckbox
										size='small'
										color='default'
										onChange={handleUserDismissed}
										checked={isChecked}
										classes={{
											root: classes?.checkbox,
											checked: classes?.checked,
										}}
									/>
								)}
							</View>
						<I18n size={isWeb ? 16 : 10}>reportView.wageDataSummary.doNotShowAgain</I18n>
					</View>
				) : null}
				{
					totalPages > 1 && (
						<View style={styles.buttonContainer}>
							<Button
								buttonStyle={[
									styles.navigateButton,
									isFirstPage ? styles.disableButton : {},
									isPrevButtonHovered && !isFirstPage ? styles.hoverButton : {},
								]}
								disabled={isFirstPage}
								onPress={prevPage}
								onMouseEnter={() => setIsPrevButtonHovered(true)}
								onMouseLeave={() => setIsPrevButtonHovered(false)}
							>
								<View style={styles.textWrapper}>
									
									{/* <I18n size={isWeb ? 16 : 12} color={isFirstPage ? 'disableButtonGrey' :
										(isPrevButtonHovered && !isFirstPage) ? 'white' : 'popupTextButton'} >
										{' > '}
									</I18n>
									 */}
									<I18n size={isWeb ? 16 : 12} color={isFirstPage ? 'disableButtonGrey' :
										(isPrevButtonHovered && !isFirstPage) ? 'white' : 'popupTextButton'}>
										modal.workerCampaign.previous
									</I18n>
									
								</View>
							</Button>
							<Button
								buttonStyle={[
									styles.navigateButton,
									isLastPage ? styles.disableButton : {},
									isNextButtonHovered && !isLastPage ? styles.hoverButton : {},
								]}
								disabled={isLastPage} 
								onPress={nextPage}
								onMouseEnter={() => setIsNextButtonHovered(true)}
								onMouseLeave={() => setIsNextButtonHovered(false)}
							>
								<View style={styles.textWrapper}>
									<I18n size={isWeb ? 16 : 12} color={ isLastPage ? 'disableButtonGrey' :
										(isNextButtonHovered && !isLastPage) ? 'white' : 'popupTextButton'}>
										modal.workerCampaign.next
									</I18n>

	
									{/* <I18n size={isWeb ? 16 : 12} color={ isLastPage ? 'disableButtonGrey' :
										(isNextButtonHovered && !isLastPage) ? 'white' : 'popupTextButton'} style={(isIos || isWeb) ? {transform: [ { rotate: '180deg' }]} : {}}>
										{' > '}
									</I18n> */}

								</View>
							</Button>
						</View>
					)
				}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	modalContainer: {	
		padding: 40,
		flexDirection: 'column',
		shadowColor: 'rgba(0, 0, 0, 0)',
		shadowOpacity: 0.1,
		shadowOffset: { width: 0, height: 2 },
		backgroundColor: colors.white,
		borderRadius: 8,
		minHeight: 500,
		justifyContent: 'space-between',
		...Platform.select({
			android: {
				minHeight: 400,
			},
			ios: {
				minHeight: 400,
			},
		}),
	},
	divider: {
		backgroundColor: colors.lightGrey,
		opacity: 1,
		height: 2,
		width: '100%',
		marginVertical: 24,
	},
	navigateButton: {
		width: 92,
		height: 32,
		borderStyle: 'solid',
		borderWidth: 1,
		borderColor: colors.popupTextButton,
		letterSpacing: 1.25 ,
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 60,
		paddingHorizontal: 16,
		...Platform.select({
			android: {
				width: 70,
				paddingHorizontal: 3,
			},
			ios: {
				width: 70,
				paddingHorizontal: 3,
			},
			web: {
				paddingHorizontal: 15,
			},
		}),
	},
	hoverButton: {
        backgroundColor: '#4AAEEF',
        borderColor: '#4AAEEF',
		color: colors.white,
        cursor: 'pointer',
    },

	textWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 8,
		...Platform.select({
			web: {
				flexDirection: 'row-reverse',
				whiteSpace: 'nowrap',
			},
			ios: {
				flexDirection: 'row-reverse',
			},
			android: {
				flexDirection: 'row',
			},
		}),
	},

	disableButton: {
		color: colors.disableButtonGrey,
		borderColor: colors.disableButtonGrey,
	},
	navigationContainer: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginTop: 20,
	},
	checkboxContainer: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		...Platform.select({
			ios: {
				gap: 4,
			}
		}),
	},
	checkboxButton: {
		// width: 24,
		// height: 24,
		marginRight: 10,
		...Platform.select({
			ios: {
				marginHorizontal: 8,
			}
		}),
		alignItems: 'center',
		justifyContent: 'center',
	},
	buttonContainer: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between', 
		color: colors.primary,
		gap: 24,
		...Platform.select({
			android: {
				width: 150,
			},
			ios: {
				width: 150,
			},
		}),
	},
	closeButton: {
		padding: 4,
		marginLeft: -8,
	},
	closeButtonText: {
		fontSize: 28,
		color: colors.darkGrey,
		lineHeight: 28,
	},
});
